/**
 * The `?invite=<token>` link an invitation email carries.
 *
 * Three things make this less trivial than reading a query parameter.
 *
 * **The token must be captured before anything else can lose it.** Signing in
 * can navigate the page — a provider redirect, or simply the user opening the
 * login form in a new tab — and the query string does not survive that. So it is
 * copied into sessionStorage on the very first load and read from there
 * afterwards. sessionStorage rather than localStorage: an invitation belongs to
 * the tab that opened it, and a token left in localStorage would still be
 * waiting weeks later in an unrelated session.
 *
 * **It must not linger in the URL.** A bearer token in the address bar gets
 * copied into chat messages, saved in history, and sent in Referer headers to
 * anything the page links out to. history.replaceState strips it as soon as it
 * has been stashed, without a reload.
 *
 * **It is cleared explicitly, not on success.** Acceptance can fail for reasons
 * worth showing (link already used, expired, already a member), and clearing on
 * the way past would replace those with a silent nothing on the next render.
 *
 * Capture runs at module load rather than inside a component, because the token
 * is now redeemed by TreeContext's bootstrap effect. Effects run child-first,
 * so anything that captured during a child's render would be racing the parent
 * that needs the value; importing this module settles it before either renders.
 */

const KEY = 'kinship_invite_token';

export function captureInviteToken() {
  try {
    const params = new URLSearchParams(window.location.search);
    const fromUrl = params.get('invite');

    if (fromUrl) {
      sessionStorage.setItem(KEY, fromUrl);
      params.delete('invite');
      const query = params.toString();
      window.history.replaceState(
        {},
        '',
        window.location.pathname + (query ? `?${query}` : '') + window.location.hash,
      );
      return fromUrl;
    }

    return sessionStorage.getItem(KEY);
  } catch {
    // Private-mode Safari throws on sessionStorage. An invitation that works
    // only until the page reloads is still better than one that crashes it.
    return null;
  }
}

/** The stashed token, without touching the URL. Safe to call repeatedly. */
export function peekInviteToken() {
  try {
    return sessionStorage.getItem(KEY);
  } catch {
    return null;
  }
}

export function clearInviteToken() {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    /* see above */
  }
}

// Runs on import, before any component renders.
captureInviteToken();

/**
 * Where an invitation tells people to go.
 *
 * Hardcoded to the `.web.app` address until 2026-08-25, when the app also
 * started answering at `app.kinshiptrees.com`. Both hostnames serve the same
 * bundle and the old one cannot be switched off, so nothing was broken by the
 * stale value — but every invitation sent from the new address was still
 * mailing relatives the Firebase URL.
 *
 * Deliberately *not* `window.location.origin`. Invites are composed in the
 * running app, and a dev machine's origin is `http://localhost:3000` — that
 * reads as harmless right up until a real invitation goes out from a dev
 * session carrying a link only that laptop can open. A build-time constant
 * cannot make that mistake. `REACT_APP_PUBLIC_ORIGIN` overrides it for a
 * staging build; production needs no `.env` entry, because the default is the
 * production address.
 */
export const PUBLIC_ORIGIN =
  process.env.REACT_APP_PUBLIC_ORIGIN || 'https://app.kinshiptrees.com';

/** The URL an invitation email carries. Without a token, just the front door. */
export function buildInviteUrl(token) {
  return token
    ? `${PUBLIC_ORIGIN}/?invite=${encodeURIComponent(token)}`
    : PUBLIC_ORIGIN;
}
